import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useNavigate, useParams } from 'react-router-dom';
import AxiosUtils from '../../utils/axios-utils';
import { API } from '../../service/api-route';
import { formatedDate } from '../../utils/common';
import PageTitle from '../../components/page-title';
import AddMembersModal from '../../components/add-members-modal';
import UserAvatar from '../../components/user-avatar';
import VerifiedTick from '../../components/verified-tick';
import { RoleBadge } from '../../components/role-badge';
import { useCurrentRoles } from '../../hooks/use-current-roles';
import { addPlayersToVote } from '../vote-manager/api';
import AdjustScoreModal from './adjust-score-modal';
import RemoveEntriesModal from './remove-entries-modal';
import ResetScoreModal from './reset-score-modal';
import ScoreLogsDrawer from './score-logs-drawer';
import { MEMBER_BADGE, VoteEntriesResponse, VoteEntry, displayName, formatScore } from './types';

/**
 * Dòng chính của cột "Thuộc": Nhóm quản lý → nhóm mình quản lý (Agency/Quản lý)
 * → Agency trong file TikTok → phòng ban.
 */
const affiliationTitle = (entry: VoteEntry) => {
  const a = entry.affiliation;
  const owns = a.owns || [];
  return (
    a.group?.title ||
    (owns.length ? `Quản lý ${owns.map((g) => g.title).join(', ')}` : '') ||
    a.fileAgency ||
    (a.department ? `Phòng ${a.department}` : '')
  );
};

/**
 * "Thuộc" gọn trong 2 dòng: nhóm/Agency quản lý, rồi trưởng nhóm · nhóm nhảy ·
 * phòng ban. Thay cột Email (không cần cho sự kiện, lộ thông tin cá nhân) và
 * dùng được cho cả Creator, NPC, nhóm nhảy lẫn Nhân viên.
 */
const AffiliationCell = ({ entry }: { entry: VoteEntry }) => {
  const a = entry.affiliation;
  const title = affiliationTitle(entry);
  const details: string[] = [];
  if (a.group && a.manager) details.push(`Trưởng nhóm: ${a.manager.name}`);
  if (a.team) details.push(`Nhóm nhảy ${a.team.name}`);
  if ((a.group || a.owns?.length) && a.department) details.push(`Phòng ${a.department}`);
  if (!a.group && a.fileAgency) details.push('Agency theo file TikTok');
  const tip: ReactNode = (
    <div>
      {a.group ? <div>Nhóm quản lý: {a.group.title}</div> : null}
      {a.manager ? (
        <div>
          Trưởng nhóm: {a.manager.name} ({a.manager.label})
        </div>
      ) : null}
      {a.team ? (
        <div>
          Nhóm nhảy: {a.team.name}
          {a.team.leaderName ? ` · trưởng nhóm ${a.team.leaderName}` : ''}
        </div>
      ) : null}
      {a.owns?.length ? <div>Đứng tên nhóm: {a.owns.map((g) => g.title).join(', ')}</div> : null}
      {a.department ? <div>Phòng ban: {a.department}</div> : null}
      {!a.group && a.fileAgency ? <div>Agency (file TikTok gần nhất): {a.fileAgency}</div> : null}
    </div>
  );
  if (!title) {
    return <Typography.Text type="secondary">Chưa thuộc nhóm nào</Typography.Text>;
  }
  return (
    <Tooltip title={tip}>
      <div style={{ minWidth: 0 }}>
        <Typography.Text ellipsis style={{ display: 'block', fontWeight: 500 }}>
          {title}
        </Typography.Text>
        {details.length ? (
          <Typography.Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>
            {details.join(' · ')}
          </Typography.Text>
        ) : null}
      </div>
    </Tooltip>
  );
};

function UserJoinVoteManager() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useCurrentRoles();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VoteEntriesResponse | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isAddPlayersOpen, setIsAddPlayersOpen] = useState(false);
  const [adjusting, setAdjusting] = useState<VoteEntry | null>(null);
  const [removing, setRemoving] = useState<VoteEntry[]>([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    AxiosUtils.get(API.voteAdminEntries(id))
      .then((res: any) => alive && setData(res?.data || null))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const items = useMemo(() => data?.items || [], [data]);
  const totalScore = useMemo(() => items.reduce((s, x) => s + (x.score || 0), 0), [items]);
  const existingUserIds = useMemo(
    () => items.map((x) => x.user?.id).filter(Boolean) as number[],
    [items]
  );
  const voteTitle = data?.vote?.title;

  const typeFilters = useMemo(
    () => Array.from(new Set(items.map((x) => x.memberLabel))).map((v) => ({ text: v, value: v })),
    [items]
  );
  const groupFilters = useMemo(
    () =>
      Array.from(new Set(items.map(affiliationTitle))).map((v) => ({
        text: v || 'Chưa thuộc nhóm nào',
        value: v,
      })),
    [items]
  );

  const columns: ColumnsType<VoteEntry> = [
    {
      title: '#',
      key: 'rank',
      width: 56,
      render: (_, __, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      title: 'Người tham gia',
      key: 'user',
      width: 260,
      render: (_, entry) => {
        const badge = MEMBER_BADGE[entry.memberType];
        return (
          <Space size={10} style={{ minWidth: 0 }}>
            <UserAvatar src={entry.user?.avatar} size={36} />
            <div style={{ minWidth: 0 }}>
              <Space size={4} style={{ maxWidth: 200 }}>
                <Typography.Text strong ellipsis style={{ maxWidth: 160 }}>
                  {displayName(entry)}
                </Typography.Text>
                {badge ? <RoleBadge kind={badge} size={16} /> : null}
              </Space>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  @{entry.user?.username}{' '}
                </Typography.Text>
                <VerifiedTick badge={entry.user?.verifiedBadge} size={12} />
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Loại',
      key: 'type',
      width: 150,
      filters: typeFilters,
      onFilter: (value, entry) => entry.memberLabel === value,
      render: (_, entry) => <Tag bordered={false}>{entry.memberLabel}</Tag>,
    },
    {
      title: 'Thuộc',
      key: 'affiliation',
      width: 260,
      filters: groupFilters,
      onFilter: (value, entry) => affiliationTitle(entry) === value,
      render: (_, entry) => <AffiliationCell entry={entry} />,
    },
    {
      title: 'Điểm',
      key: 'score',
      width: 140,
      sorter: (a, b) => a.score - b.score,
      render: (_, entry) => (
        <Space size={6}>
          <Typography.Text strong>{formatScore(entry.score)}</Typography.Text>
          {entry.manual.count ? (
            <Tooltip
              title={`Đã chỉnh tay ${entry.manual.count} lần, tổng ${entry.manual.total >= 0 ? '+' : ''}${formatScore(entry.manual.total)} điểm`}
            >
              <Tag
                color={entry.manual.total >= 0 ? 'green' : 'red'}
                bordered={false}
                style={{ marginInlineEnd: 0 }}
              >
                {entry.manual.total >= 0 ? '+' : '−'}
                {formatScore(Math.abs(entry.manual.total))}
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Ngày tham gia',
      key: 'createdAt',
      width: 130,
      render: (_, entry) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatedDate(entry.createdAt)}</span>
      ),
    },
  ];

  if (isAdmin) {
    columns.push({
      title: '',
      key: 'action',
      width: 56,
      fixed: 'right',
      render: (_, entry) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'adjust', label: 'Cộng / trừ điểm', icon: <EditOutlined /> },
              { type: 'divider' },
              {
                key: 'remove',
                label: 'Loại khỏi danh sách',
                icon: <DeleteOutlined />,
                danger: true,
              },
            ],
            onClick: ({ key }) => (key === 'adjust' ? setAdjusting(entry) : setRemoving([entry])),
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            aria-label={`Thao tác với ${displayName(entry)}`}
          />
        </Dropdown>
      ),
    });
  }

  const selectedEntries = items.filter((x) => selectedRowKeys.includes(x.id));

  return (
    <div>
      <PageTitle
        title="Danh sách"
        description={
          voteTitle ? (
            <>
              Sự kiện <b>{voteTitle}</b> · {items.length} người tham gia · tổng{' '}
              {formatScore(totalScore)} điểm
              {data?.vote?.score ? ` · mỗi phiếu +${formatScore(data.vote.score)}` : ''}
            </>
          ) : undefined
        }
        extra={
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/vote-management')}>
              Danh sách sự kiện
            </Button>
            <Button icon={<HistoryOutlined />} onClick={() => setLogsOpen(true)}>
              Lịch sử điều chỉnh
            </Button>
            {isAdmin ? (
              <Button
                danger
                icon={<ReloadOutlined />}
                disabled={!items.length}
                onClick={() => setResetOpen(true)}
              >
                Reset điểm
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddPlayersOpen(true)}
            >
              Creator
            </Button>
          </Space>
        }
      />

      <div className="custom-table custom-form">
        {isAdmin && selectedEntries.length ? (
          <Space className="mb-10">
            <Button danger icon={<DeleteOutlined />} onClick={() => setRemoving(selectedEntries)}>
              Loại khỏi danh sách ({selectedEntries.length})
            </Button>
            <Button type="link" onClick={() => setSelectedRowKeys([])}>
              Bỏ chọn
            </Button>
          </Space>
        ) : null}
        <Table
          rowSelection={isAdmin ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          scroll={{ x: 1100 }}
          loading={loading}
          locale={{ emptyText: 'Chưa có ai tham gia — bấm "+ Creator" để thêm.' }}
        />
      </div>

      <AdjustScoreModal
        entry={adjusting}
        scorePerVote={data?.vote?.score || 0}
        onClose={() => setAdjusting(null)}
        onDone={() => {
          setAdjusting(null);
          reload();
        }}
      />
      <RemoveEntriesModal
        voteId={id}
        entries={removing}
        onClose={() => setRemoving([])}
        onDone={() => {
          setRemoving([]);
          setSelectedRowKeys([]);
          reload();
        }}
      />
      <ResetScoreModal
        open={resetOpen}
        voteId={id}
        voteTitle={voteTitle}
        people={items.length}
        totalScore={totalScore}
        onClose={() => setResetOpen(false)}
        onDone={() => {
          setResetOpen(false);
          reload();
        }}
      />
      <ScoreLogsDrawer open={logsOpen} voteId={id} onClose={() => setLogsOpen(false)} />

      <AddMembersModal
        open={isAddPlayersOpen}
        title="+ Creator"
        targetLabel="Sự kiện bình chọn"
        target={{ value: Number(id), label: voteTitle || `Sự kiện #${id}` }}
        userLabel="Creator"
        existingUserIds={existingUserIds}
        submitText="Thêm vào sự kiện"
        onSubmit={(userIds, voteId) => addPlayersToVote(voteId, userIds)}
        onSuccess={() => {
          setIsAddPlayersOpen(false);
          reload();
        }}
        onCancel={() => setIsAddPlayersOpen(false)}
      />
    </div>
  );
}

export default UserJoinVoteManager;
